import { Dispatch, FC, useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import lang from '../../lib/language';
import { TradingPlanTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import {
  activateDemoAccount,
  setUseDemoAccount as setUseDemoAccountCall,
} from '../../lib/store/crm';
import { CRMCall } from '../../lib/store/crm/types';
import {
  isCallStatusError,
  isCallStatusReady,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import DemoAccountImage from '../../assets/img/demo-account.png';
import { GTM_CATEGORIES, GTM_DEMO_ACCOUNT, GTM_EVENTS } from '../../constants/gtm';
import { useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { hashSHA256 } from '../../util/hash';
import { UI_ROUTES } from '../../util/routes';
import { BaseModal, MobileDrawer } from '../index';

import styles from './DemoAccountActivationPopup.module.scss';

type OwnProps = {
  setIsDisplayModal: (status: boolean) => void;
  isDisplayModal: boolean;
};

type Props = OwnProps & ReduxProps;

const DemoAccountActivationPopup: FC<Props> = (props: Props): JSX.Element => {
  const {
    isDisplayModal,
    activateAccount,
    isActivateDemoAccountFailed,
    setIsDisplayModal,
    setUseDemoAccount,
    isActivateAccountReady,
    userId,
    userEmail,
    tradingPlans,
  } = props;

  const hasTrackedDemoInit = useRef<boolean>(false);
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (isActivateDemoAccountFailed) {
      setIsDisplayModal(false);
      navigate(UI_ROUTES.demoAccountActivationError, { replace: true });
    }
  }, [isActivateDemoAccountFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isActivateAccountReady) {
      setUseDemoAccount(true);
      setIsDisplayModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivateAccountReady]);

  const trackDemoAccountInitiated = async () => {
    if (hasTrackedDemoInit.current) return;
    hasTrackedDemoInit.current = true;
    const demoPlan = tradingPlans.find((p) => p.plan_type === TradingPlanTypeEnum.Demo);
    const hashedEmail = await hashSHA256(userEmail || '');
    TagManager.dataLayer({
      dataLayer: {
        event: GTM_EVENTS.DEMO_ACCOUNT_INITIATED,
        event_category: GTM_CATEGORIES.ACCOUNT_MANAGEMENT,
        event_action: GTM_DEMO_ACCOUNT.ACTION,
        event_label: GTM_DEMO_ACCOUNT.LABEL,
        client_id: userId,
        user_email_hashed: hashedEmail,
        demo_account_size: demoPlan?.daily_hard_bp,
      },
    });
  };

  const renderContent = () => (
    <div className={styles.container}>
      <div className={styles.content}>
        <img src={DemoAccountImage} alt="demo-account" />
        <div className={styles.title}>{lang.commonActivatingDemoAccountTitle()}</div>
        <div className={styles.descriptipon}>{lang.commonActivatingDemoAccountDesc()}</div>
      </div>
      <div className={styles.buttonsWrapper}>
        <Button
          className={styles.button}
          variant={ButtonTypesEnum.Primary}
          onClick={() => {
            trackDemoAccountInitiated();
            activateAccount();
            setIsDisplayModal(false);
          }}
        >
          {lang.commonActivateButton()}
        </Button>
        <Button
          className={styles.tryDemoButton}
          variant={ButtonTypesEnum.Secondary}
          onClick={() => setIsDisplayModal(false)}
        >
          {lang.commonCancelButton()}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <MobileDrawer title="" onClose={() => setIsDisplayModal(false)} isOpen={isDisplayModal}>
        {renderContent()}
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={isDisplayModal} toggle={() => setIsDisplayModal(false)}>
      {renderContent()}
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isActivateDemoAccountFailed: isCallStatusError(
    CRMCall.activateDemoAccount,
    state.crm.statusByCall,
  ),
  isActivateAccountReady: isCallStatusReady(CRMCall.activateDemoAccount, state.crm.statusByCall),
  userId: state.auth.enroll.userId,
  userEmail: state.crm.individualExtendedInfo?.email,
  tradingPlans: state.crm.tradingPlans,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  activateAccount: () => dispatch(activateDemoAccount()),
  setUseDemoAccount: (value: boolean) => dispatch(setUseDemoAccountCall(value)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DemoAccountActivationPopup);
